import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, curSym, fmtDate } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { StatusBadge } from "@finance/components/StatusBadge";
import { useAuth } from "@finance/context/AuthContext";
import { Receipt, Money, Tray, ListChecks, Warning, Truck, Gauge, Wallet, HandCoins, Coins, CaretDown, ClipboardText } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { CategoryBadge, PriorityBadge } from "@finance/components/ActionBits";
import { OpsTiles } from "@finance/components/OpsTiles";
import { ApprovalsQueue } from "@finance/components/ApprovalsQueue";

function CountUp({ text }) {
  const str = String(text ?? "");
  const m = str.match(/-?[\d,]*\.?\d+/);
  const [disp, setDisp] = useState(m ? str.replace(m[0], "0") : str);
  useEffect(() => {
    if (!m) { setDisp(str); return; }
    const raw = m[0].replace(/,/g, "");
    const target = parseFloat(raw);
    if (!isFinite(target)) { setDisp(str); return; }
    const decimals = raw.includes(".") ? raw.split(".")[1].length : 0;
    let start, frame;
    const dur = 650;
    const step = (t) => {
      if (start === undefined) start = t;
      const p = Math.min(1, (t - start) / dur);
      const cur = target * (1 - Math.pow(1 - p, 3));
      setDisp(str.replace(m[0], cur.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [str]);
  return <>{disp}</>;
}

// Auto-fit big KPI numbers to the card width by stepping the font size down as the string grows.
function fitClass(value) {
  const len = String(value ?? "").length;
  if (len > 22) return "text-base";
  if (len > 18) return "text-lg";
  if (len > 15) return "text-xl";
  if (len > 12) return "text-2xl";
  return "text-3xl";
}

function Kpi({ icon: Icon, label, value, sub, testid, tone = "default", to }) {
  const navigate = useNavigate();
  const toneCls = tone === "accent" ? "text-[#1B4383]" : tone === "warn" ? "text-amber-600" : "text-gray-900 dark:text-white";
  const iconTint = tone === "warn"
    ? "bg-amber-500/10 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
    : "bg-[#1B4383]/10 text-[#1B4383] dark:bg-[#1B4383]/20 dark:text-[#F0A868]";
  return (
    <div data-testid={testid} onClick={to ? () => navigate(to) : undefined} className={`rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md${to ? " cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between">
        <span className="lx-label">{label}</span>
        <span className={`grid h-9 w-9 place-items-center rounded-lg ${iconTint}`}><Icon size={18} weight="bold" /></span>
      </div>
      <div className={`mt-3 font-display ${fitClass(value)} font-bold lx-num tracking-tight ${toneCls}`}><CountUp text={value} /></div>
      {sub && <div className="mt-1.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

const PANEL_TONES = {
  amber: { wrap: "border-amber-200 border-l-amber-500 bg-amber-50/50", head: "border-amber-200", divide: "divide-amber-100", icon: "text-amber-600" },
  orange: { wrap: "border-[#C9D6EA] border-l-[#1B4383] bg-[#E6ECF5]/50", head: "border-[#C9D6EA]", divide: "divide-[#E6ECF5]", icon: "text-[#1B4383]" },
  red: { wrap: "border-red-200 border-l-red-500 bg-red-50/50", head: "border-red-200", divide: "divide-red-100", icon: "text-red-600" },
  blue: { wrap: "border-blue-200 border-l-blue-500 bg-blue-50/50", head: "border-blue-200", divide: "divide-blue-100", icon: "text-blue-600" },
};

function CollapsePanel({ tone = "amber", icon: Icon, title, testid, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const t = PANEL_TONES[tone];
  return (
    <div className={`mt-6 overflow-hidden rounded-xl border border-l-4 shadow-sm ${t.wrap}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 border-b px-5 py-3 text-left transition-colors hover:bg-black/[0.02] ${t.head}${open ? "" : " border-b-transparent"}`}
      >
        <Icon size={16} className={t.icon} />
        <h3 className="font-display text-base font-bold text-gray-900">{title}</h3>
        <CaretDown size={16} weight="bold" className={`ml-auto text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className={`divide-y ${t.divide}`}>{children}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [imp, setImp] = useState({ outstanding: [], variance_alerts: [] });
  const [fx, setFx] = useState(null);
  const [bdTrips, setBdTrips] = useState([]);
  const [pendingAuth, setPendingAuth] = useState({ total: 0, grv_count: 0, dn_count: 0, grvs: [], delivery_notes: [], can_authorize: false });
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/dashboard/import-outstanding").then((r) => setImp(r.data)).catch(() => setImp({ outstanding: [], variance_alerts: [] }));
    api.get("/fx/exposure").then((r) => setFx(r.data)).catch(() => {});
    api.get("/reports/trips", { params: { status: "Active" } }).then((r) => setBdTrips(r.data)).catch(() => {});
    api.get("/fulfillment/pending-auth").then((r) => setPendingAuth(r.data)).catch(() => {});
  }, []);

  const chartData = data
    ? Object.entries(data.status_counts).map(([k, v]) => ({ name: k.replace(/ /g, "\n"), short: k, value: v }))
    : [];

  return (
    <Layout>
      <PageHeader title="Dashboard" subtitle={`Welcome back, ${user?.name}`} />
      <OpsTiles />
      <div className="p-8">
        {!data ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6 kpi-grid">
              <div data-testid="kpi-total-cash" onClick={() => navigate("/cash-position")}
                className="cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="lx-label">Total Cash</span>
                  <Wallet size={18} className="text-gray-400" />
                </div>
                <div className={`mt-3 font-display ${fitClass("TZS " + fmtNum(data.total_cash_tzs || 0))} font-bold lx-num tracking-tight ${data.total_cash_tzs < 0 ? "text-red-600" : "text-emerald-700"}`}>TZS <CountUp text={fmtNum(data.total_cash_tzs || 0)} /></div>
                <div className="mt-1 text-xs text-gray-500">Across all banks (TZS equiv.)</div>
              </div>
              <Kpi icon={Receipt} label="Open Transactions" value={data.open_transactions} to="/transactions" testid="kpi-open-transactions" />
              <Kpi icon={Money} label="Payables (Total)" value={`TSh ${fmtNum(data.payables_total)}`} sub={`Trade ${fmtNum(data.trade_payables_total || 0)} + Expense ${fmtNum(data.expense_payable_total || 0)}${data.payables_overdue_count ? ` · ${data.payables_overdue_count} overdue` : ""}`} to="/payables" testid="kpi-payables" tone={data.payables_overdue_count ? "warn" : "default"} />
              <Kpi icon={Tray} label="Pending Documents" value={data.pending_documents} to="/documents" testid="kpi-pending-docs" />
              <Kpi icon={ListChecks} label="My Open Actions" value={data.my_open_actions} to="/my-actions" testid="kpi-my-actions" />
              <Kpi icon={Warning} label="Stale Transactions" value={data.stale_transactions.length} sub=">5 days no change" to="/transactions" testid="kpi-stale" tone="warn" />
            </div>

            <ApprovalsQueue />
            {pendingAuth.total > 0 && (
              <div data-testid="tile-pending-auth" className="mt-4 overflow-hidden rounded-xl border border-amber-200 border-l-4 border-l-amber-500 bg-amber-50/60 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-amber-100 text-amber-600"><ClipboardText size={20} weight="fill" /></span>
                    <div>
                      <div className="font-display text-lg font-bold text-gray-900">Awaiting Authorization · <span data-testid="pending-auth-total">{pendingAuth.total}</span></div>
                      <div className="text-xs text-gray-500">
                        <span className="font-semibold text-amber-700">{pendingAuth.grv_count} GRV</span>
                        {" · "}
                        <span className="font-semibold text-amber-700">{pendingAuth.dn_count} Delivery Note</span>
                        {pendingAuth.can_authorize ? " — you can authorize these" : " — pending Ronick's sign-off"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[
                    ...pendingAuth.grvs.map((g) => ({ ...g, kind: "GRV", no: g.grv_no })),
                    ...pendingAuth.delivery_notes.map((d) => ({ ...d, kind: "DN", no: d.dn_no })),
                  ].slice(0, 8).map((x) => (
                    <span key={x.id} data-testid={`pending-auth-chip-${x.id}`} onClick={() => navigate(`/transactions/${x.transaction_id}`)}
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs hover:bg-amber-50">
                      <span className="rounded-sm bg-amber-100 px-1 text-[10px] font-bold text-amber-700">{x.kind}</span>
                      <b className="text-gray-700">{x.no || "—"}</b>
                      {x.lx_po_no && <span className="text-gray-400">{x.lx_po_no}</span>}
                      {x.resubmit_note && <span className="text-[#1B4383]" title={`Resubmitted — ${x.resubmit_note}`}>✎</span>}
                    </span>
                  ))}
                  {pendingAuth.total > 8 && <span className="self-center text-xs text-gray-400">+{pendingAuth.total - 8} more</span>}
                </div>
              </div>
            )}

            {bdTrips.length > 0 && (
              <div data-testid="bd-spend-widget" onClick={() => navigate("/reports/trips")}
                className="mt-4 cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Money size={16} className="text-[#1B4383]" /><span className="lx-label">BD Spend — Active Trips</span></div>
                  <span className="text-xs text-gray-500">{bdTrips.length} active trip{bdTrips.length === 1 ? "" : "s"} · TSh {fmtNum(bdTrips.reduce((s, t) => s + (t.total_tzs || 0), 0))}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {bdTrips.map((t) => (
                    <span key={t.id} data-testid={`bd-trip-tile-${t.id}`} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-2.5 py-1 text-xs">
                      <b>{t.trip_no}</b> <span className="max-w-[160px] truncate text-gray-500">{t.name}</span>
                      <span className="font-semibold text-gray-900">TSh {fmtNum(t.total_tzs)}</span>
                      <span className="text-gray-400">· {t.expense_count} exp</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {fx && (fx.by_currency.length > 0 || Math.abs(fx.realized_net) > 0.005) && (
              <div data-testid="fx-exposure-widget" onClick={() => navigate("/reports/forex")}
                className="mt-4 cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><Money size={16} className="text-[#1B4383]" /><span className="lx-label">FX Exposure</span></div>
                  <span className="text-xs text-gray-500">
                    Unrealized <b className={fx.unrealized_net >= 0 ? "text-emerald-600" : "text-red-600"}>TSh {fmtNum(fx.unrealized_net)}</b>
                    {" · "}Realized YTD <b className={fx.realized_net >= 0 ? "text-emerald-600" : "text-red-600"}>TSh {fmtNum(fx.realized_net)}</b>
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {fx.by_currency.length === 0 && <span className="text-xs text-gray-400">No open foreign-currency balances.</span>}
                  {fx.by_currency.map((c) => (
                    <span key={c.currency} data-testid={`fx-cur-${c.currency}`} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-2.5 py-1 text-xs">
                      <b>{c.currency}</b> {fmtNum(c.foreign_net)} <span className="text-gray-400">→ TSh {fmtNum(c.market_tzs)}</span>
                      <span className={c.unrealized >= 0 ? "text-emerald-600" : "text-red-600"}>({fmtNum(c.unrealized)})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {data.attention_count > 0 && (
              <div data-testid="tile-needs-attention" onClick={() => navigate("/my-actions")}
                className="mt-4 cursor-pointer overflow-hidden rounded-xl border border-red-200 border-l-4 border-l-red-500 bg-red-50/60 p-4 shadow-sm transition-all duration-200 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-red-100 text-red-600"><Warning size={20} weight="fill" /></span>
                    <div>
                      <div className="font-display text-lg font-bold text-gray-900">Needs Attention · {data.attention_count}</div>
                      <div className="text-xs text-gray-500">
                        <span data-testid="attention-overdue-count" className="font-semibold text-red-600">{data.overdue_actions_count} overdue</span>
                        {" · "}
                        <span data-testid="attention-high-count" className="font-semibold text-amber-600">{data.high_priority_open} high-priority</span>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-red-600">View my actions →</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {data.attention_actions.slice(0, 4).map((a) => (
                    <span key={a.id} data-testid={`attention-chip-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs">
                      {a.is_overdue
                        ? <span className="rounded-sm bg-red-100 px-1 text-[10px] font-bold text-red-700">OVERDUE</span>
                        : <span className="rounded-sm bg-amber-100 px-1 text-[10px] font-bold text-amber-700">HIGH</span>}
                      <span className="max-w-[240px] truncate text-gray-700">{a.details}</span>
                      {a.due_date && <span className="lx-num text-gray-400">{fmtDate(a.due_date)}</span>}
                    </span>
                  ))}
                  {data.attention_count > 4 && <span className="self-center text-xs text-gray-400">+{data.attention_count - 4} more</span>}
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div data-testid="tile-prepayments-open" onClick={() => navigate("/advances")}
                className="cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="lx-label">Open Prepayments — to suppliers</span>
                  <Coins size={18} className="text-gray-400" />
                </div>
                <div className={`mt-3 font-display ${fitClass("TSh " + fmtNum(data.prepayments_open || 0))} font-bold lx-num tracking-tight text-gray-900`}>TSh {fmtNum(data.prepayments_open || 0)}</div>
                <div className="mt-1 text-xs text-gray-500">Unrecognized balance · GL 1450</div>
              </div>
              <div data-testid="tile-advance-receipts-open" onClick={() => navigate("/advances")}
                className="cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="lx-label">Open Advance Receipts — from clients</span>
                  <HandCoins size={18} className="text-gray-400" />
                </div>
                <div className={`mt-3 font-display ${fitClass("TSh " + fmtNum(data.advance_receipts_open || 0))} font-bold lx-num tracking-tight text-emerald-700`}>TSh {fmtNum(data.advance_receipts_open || 0)}</div>
                <div className="mt-1 text-xs text-gray-500">Unrecognized balance · GL 2110</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div data-testid="tile-backorder-deliver" onClick={() => navigate("/reports/po-health")}
                className="cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="lx-label">Received · Awaiting Delivery</span>
                  <Truck size={18} className="text-gray-400" />
                </div>
                <div className={`mt-3 font-display text-3xl font-bold lx-num tracking-tight ${data.backorder_receive_deliver ? "text-amber-600" : "text-gray-900"}`}>{data.backorder_receive_deliver}</div>
                <div className="mt-1 text-xs text-gray-500">POs with goods in but not fully delivered</div>
              </div>
              <div data-testid="tile-backorder-invoice" onClick={() => navigate("/reports/po-health")}
                className="cursor-pointer rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="lx-label">Delivered · Awaiting Invoice</span>
                  <Gauge size={18} className="text-gray-400" />
                </div>
                <div className={`mt-3 font-display text-3xl font-bold lx-num tracking-tight ${data.backorder_deliver_invoice ? "text-amber-600" : "text-gray-900"}`}>{data.backorder_deliver_invoice}</div>
                <div className="mt-1 text-xs text-gray-500">POs delivered but not fully invoiced</div>
              </div>
            </div>

            {(data.partial_delivery_overdue || []).length > 0 && (
              <CollapsePanel tone="amber" icon={Truck} testid="partial-delivery-panel" title={`Part-Delivered & Overdue (${data.partial_delivery_overdue.length})`}>
                  {data.partial_delivery_overdue.map((t) => (
                    <div key={t.id} data-testid={`partial-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}
                      className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-sm hover:bg-amber-50">
                      <span className="text-gray-800"><span className="font-mono-num font-medium">{t.lx_po_no || "—"}</span>{t.client_name ? ` · ${t.client_name}` : ""}</span>
                      <span className="flex items-center gap-4">
                        <span className="lx-num text-gray-600">{t.delivered_pct?.toFixed(0)}% delivered</span>
                        <span className="lx-num font-semibold text-amber-700">ETA {fmtDate(t.eta)}</span>
                      </span>
                    </div>
                  ))}
              </CollapsePanel>
            )}

            {(data.stale_partial_pos || []).length > 0 && (
              <CollapsePanel tone="orange" icon={Truck} testid="stale-partial-po-panel" title={`Half-Shipped & Stalling — Client POs (${data.stale_partial_pos.length})`}>
                  {data.stale_partial_pos.map((p) => (
                    <div key={p.id} data-testid={`stale-partial-${p.id}`} onClick={() => navigate("/client-pos")}
                      className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-sm hover:bg-[#E6ECF5]">
                      <span className="text-gray-800"><span className="font-mono-num font-medium">{p.client_po_no || "—"}</span>{p.client_name ? ` · ${p.client_name}` : ""}</span>
                      <span className="flex items-center gap-4">
                        <span className="badge badge-warning">Partially Fulfilled</span>
                        <span className="lx-num font-semibold text-amber-700">{p.days_stale}d idle</span>
                      </span>
                    </div>
                  ))}
              </CollapsePanel>
            )}

            {imp && (imp.variance_alerts || []).length > 0 && (
              <CollapsePanel tone="red" icon={Warning} testid="cost-variance-panel" title={`Landed-Cost Variance Alerts (${imp.variance_alerts.length})`}>
                  {imp.variance_alerts.map((t) => (
                    <div key={t.id} data-testid={`variance-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}
                      className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-sm hover:bg-red-50">
                      <span className="text-gray-800"><span className="font-mono-num font-medium">{t.lx_po_no || "—"}</span>{t.client_name ? ` · ${t.client_name}` : ""}</span>
                      <span className="flex items-center gap-4">
                        <span className="lx-num text-gray-600">Cost TSh {fmtNum(t.landed_cost)} vs Rev TSh {fmtNum(t.client_revenue)}</span>
                        <span className="lx-num font-semibold text-red-700">−TSh {fmtNum(Math.abs(t.variance))}</span>
                      </span>
                    </div>
                  ))}
              </CollapsePanel>
            )}

            {imp && imp.outstanding.length > 0 && (
              <CollapsePanel tone="blue" icon={Tray} testid="import-outstanding-panel" title={`Import POs · Outstanding Documents (${imp.outstanding.length})`}>
                  {imp.outstanding.map((t) => (
                    <div key={t.id} data-testid={`import-outstanding-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}
                      className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-sm hover:bg-blue-50">
                      <span className="text-gray-800">
                        <span className="font-mono-num font-medium">{t.lx_po_no || "—"}</span>{t.supplier_name ? ` · ${t.supplier_name}` : ""}
                        <span className="ml-2 text-xs text-gray-500">({t.shipment_mode || "—"})</span>
                      </span>
                      <span className="flex items-center gap-4">
                        <span className="hidden max-w-md truncate text-xs text-gray-500 md:inline">Missing: {t.missing.join(", ")}</span>
                        <span className="badge badge-info">{t.captured}/{t.total} docs</span>
                      </span>
                    </div>
                  ))}
              </CollapsePanel>
            )}

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm lg:col-span-2">
                <h3 className="font-display text-base font-bold text-gray-900">Transactions by Stage</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 40, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF0F3" />
                      <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={(v) => (v && v.length > 14 ? v.slice(0, 13) + "…" : v)} angle={-25} textAnchor="end" interval={0} height={64} tickMargin={6} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: "rgba(217,92,20,0.06)" }} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(16,24,40,0.08)" }} labelFormatter={(_, p) => (p && p[0] ? p[0].payload.short : "")} formatter={(v) => [v, "Transactions"]} />
                      <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={48}>
                        {chartData.map((_, i) => (
                          <Cell key={i} fill="#1B4383" />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200/70 bg-white p-5 shadow-sm">
                <h3 className="font-display text-base font-bold text-gray-900">Open Actions by User</h3>
                <div className="mt-4 space-y-3">
                  {data.user_action_counts.map((u) => (
                    <div key={u.name} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{u.name}</span>
                      <span className="lx-num font-medium text-gray-900">{u.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200/70 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-5 py-3">
                <h3 className="font-display text-base font-bold text-gray-900">My Overdue &amp; High-Priority Tasks ({(data.attention_actions || []).length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {(data.attention_actions || []).length === 0 && <div className="px-5 py-6 text-center text-sm text-gray-400">Nothing needs attention. ✅</div>}
                {(data.attention_actions || []).map((a) => (
                  <div key={a.id} data-testid={`attention-row-${a.id}`} onClick={() => navigate("/my-actions")} className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-sm hover:bg-gray-50">
                    <span className="flex items-center gap-2">
                      {a.is_overdue
                        ? <span className="rounded-sm bg-red-100 px-1 text-[10px] font-bold text-red-700">OVERDUE</span>
                        : <span className="rounded-sm bg-amber-100 px-1 text-[10px] font-bold text-amber-700">HIGH</span>}
                      <span className="text-gray-800">{a.details}</span>
                      <CategoryBadge category={a.category} />
                      <PriorityBadge priority={a.priority} />
                    </span>
                    {a.due_date && <span className={`lx-num font-semibold ${a.is_overdue ? "text-red-600" : "text-gray-500"}`}>Due {fmtDate(a.due_date)}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200/70 bg-white shadow-sm">
              <div className="border-b border-gray-200 px-5 py-3">
                <h3 className="font-display text-base font-bold text-gray-900">Stale Transactions (no status change &gt; 5 days)</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="lx-label border-b border-gray-200">
                    <th className="px-5 py-2 text-left">Somcore PO</th>
                    <th className="px-5 py-2 text-left">Supplier</th>
                    <th className="px-5 py-2 text-left">Status</th>
                    <th className="px-5 py-2 text-right">Payable</th>
                    <th className="px-5 py-2 text-left">Assigned</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stale_transactions.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">Nothing stale. 🎯</td></tr>
                  )}
                  {data.stale_transactions.map((t) => (
                    <tr key={t.id} data-testid={`stale-row-${t.id}`} onClick={() => navigate(`/transactions/${t.id}`)}
                      className="cursor-pointer border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-2 font-mono-num">{t.lx_po_no || "—"}</td>
                      <td className="px-5 py-2">{t.supplier_name || "—"}</td>
                      <td className="px-5 py-2"><StatusBadge status={t.status} /></td>
                      <td className="px-5 py-2 text-right lx-num">{curSym(t.currency)} {fmtNum(t.payable_amount)}</td>
                      <td className="px-5 py-2">{t.assigned_to_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
